import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from '@src/models';
import { fixRouteParam } from '@src/utils';
import { BaseResizableComponent } from '@src/app/modules/base';
import { ActionItemModel } from '@src/app/modules/actions';
import { SearchUsersService } from '@src/app/modules/search-users';
import { AuthUserService } from '@src/app/modules/auth';

import { ApiService, InternalMessengerService, StoreService, PermissionService } from './services';
import { ChatModel } from './chat';
import { CreateDialogService } from './chat/chat-create-form';

@Component({
  selector: 'app-internal-messenger',
  templateUrl: './internal-messenger.component.html',
  styleUrls: ['./internal-messenger.component.scss', './styles.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class InternalMessengerComponent extends BaseResizableComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly translateService = inject(TranslateService);
  private readonly apiService = inject(ApiService);
  private readonly store = inject(StoreService);
  private readonly createDialogService = inject(CreateDialogService);
  private readonly searchUsersService = inject(SearchUsersService);
  private readonly internalMessengerService = inject(InternalMessengerService);
  private readonly permService = inject(PermissionService);
  private readonly authUserService = inject(AuthUserService);

  @Input() excludeChatIds: string[] = [];

  chats?: ChatModel[];
  selectedChatId?: string;

  openMenu: boolean = false;
  createMenuItems: ActionItemModel[] = [
    {
      title: this.translateService.instant('components.internalMessenger.buttons.createGroupChat'),
      icon: 'users',
      visible: this.permService.allowGroupChatCreating$.value,
      action: () => this.openCreateGroupDialog(),
    },
    {
      title: this.translateService.instant('components.internalMessenger.buttons.createChat'),
      icon: 'user',
      visible: this.permService.allowPrivateChatCreating$.value,
      action: () => this.openSearchUserDialog(),
    },
  ];

  searchQuery: string = '';
  searchQuery$: BehaviorSubject<string> = new BehaviorSubject('');
  searchChats?: ChatModel[];

  loading = false;

  get showFullPage(): boolean {
    return !this.selectedChatId || this.isLargeScreen;
  }

  get authUserId() {
    return this.authUserService.authUser$.value?.id;
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.route.paramMap.pipe(takeUntil(this.destroyed$$)).subscribe(params => {
      this.onChangeSelectedChatId(fixRouteParam(params.get('chatId')));
    });

    this.store
      .getAttachedChats(this.authUserId)
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(chats => {
        this.chats = chats
          .filter(chat => !this.excludeChatIds.includes(chat.chatId))
          .sort((a, b) => (b.lastChatMessage?.timestamp ?? 0) - (a.lastChatMessage?.timestamp ?? 0));
        this.cdr.markForCheck();
      });

    this.getChats();

    this.searchQuery$.pipe(takeUntil(this.destroyed$$)).subscribe(searchQuery => {
      if (searchQuery === '') {
        this.searchChats = undefined;
        return;
      }

      if (this.chats) {
        this.searchChats = [
          ...this.chats.filter(
            chat =>
              chat.chatName.toLowerCase().includes(searchQuery.toLowerCase()) ||
              chat.chatDescription?.toLowerCase().includes(searchQuery.toLowerCase()),
          ),
        ];
      }
    });
  }

  getChats() {
    if (!this.authUserId) {
      return; // TODO: вывести ошибку
    }

    this.apiService.getChatsForCurrentUser(this.authUserId).subscribe();
  }

  onChangeSelectedChatId(id?: string): void {
    this.selectedChatId = id;
    this.cdr.markForCheck();
  }

  onClickCreateMenuItem(menuItem: MenuItem): void {
    this.openMenu = false;
    menuItem.action?.();
    this.cdr.markForCheck();
  }

  onClickReturnButton(): void {
    this.selectedChatId = undefined;
  }

  searchQueryChangeHandler(searchQuery: string): void {
    this.searchQuery = searchQuery;
    this.searchQuery$.next(searchQuery);
  }

  private openCreateGroupDialog(): void {
    this.createDialogService.open('Создание чата').subscribe(id => {
      if (id) {
        this.onChangeSelectedChatId(id);
      }
    });
    this.cdr.markForCheck();
  }

  private openSearchUserDialog(): void {
    // TODO: Исключить пользователей, с которыми уже создан чат
    const excludeUsersIds = this.authUserId ? [this.authUserId] : undefined;

    this.searchUsersService
      .searchSingle(undefined, undefined, excludeUsersIds, undefined, undefined, undefined, 'common.buttons.create')
      .subscribe(user => {
        this.internalMessengerService.openChatWithUser(user);
      });
  }
}
